// src/pages/student/_brand.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/* ---------------- color helpers ---------------- */
export const hexToRgba = (hex, a = 1) => {
  const c = normalizeHexAny(hex) || "#0d6efd";
  const n = parseInt(c.slice(1, 7), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const hexToRgbParts = (hex) => {
  const c = normalizeHexAny(hex) || "#0d6efd";
  const n = parseInt(c.slice(1, 7), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const clamp = (v) => Math.max(0, Math.min(255, v));
export const shade = (hex, percent) => {
  const { r, g, b } = hexToRgbParts(hex);
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  const nr = clamp(Math.round((t - r) * p) + r);
  const ng = clamp(Math.round((t - g) * p) + g);
  const nb = clamp(Math.round((t - b) * p) + b);
  return `#${[nr, ng, nb].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
};

/** Accepts #RGB, #RRGGBB, #RRGGBBAA (drops AA). Returns normalized #rrggbb or null. */
const normalizeHexAny = (h) => {
  let v = String(h || "").trim();
  if (!v) return null;
  if (v[0] !== "#") v = `#${v}`;
  if (v.length === 4) {
    // #RGB -> #RRGGBB
    v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  } else if (v.length === 9) {
    // #RRGGBBAA -> #RRGGBB
    v = v.slice(0, 7);
  }
  if (v.length !== 7) return null;
  if (!/^#[0-9a-f]{6}$/i.test(v)) return null;
  return v.toLowerCase();
};
const normalizeHex = (h, fallback = "#0d6efd") => normalizeHexAny(h) || fallback;

/* ------------- scoping helpers ------------- */
const resolveRootEl = (scope) => {
  if (typeof document === "undefined") return null;
  if (!scope) return document.documentElement; // global (legacy behavior)
  if (typeof scope === "string") return document.querySelector(scope) || document.documentElement;
  if (scope?.current) return scope.current;
  if (scope?.nodeType === 1) return scope;
  return document.documentElement;
};

const readCssHex = (el, name) => normalizeHexAny(getComputedStyle(el).getPropertyValue(name).trim());

/* ------------- push colors to CSS variables ------------- */
const applyBrand = (primaryHex, accentHex, scopeEl) => {
  if (typeof document === "undefined") return;
  const el = resolveRootEl(scopeEl);
  if (!el) return;

  const P = normalizeHex(primaryHex);
  const A = normalizeHex(accentHex || shade(P, -10));

  const { r: pr, g: pg, b: pb } = hexToRgbParts(P);
  const { r: ar, g: ag, b: ab } = hexToRgbParts(A);
  const prgb = `${pr}, ${pg}, ${pb}`;
  const argb = `${ar}, ${ag}, ${ab}`;

  // Scoped custom vars for your UI
  el.style.setProperty("--brand-primary", P);
  el.style.setProperty("--brand-primary-rgb", prgb);
  el.style.setProperty("--brand-accent", A);
  el.style.setProperty("--brand-accent-rgb", argb);
  el.style.setProperty("--brand-muted", "var(--bs-secondary-color)");

  // Scoped Bootstrap vars (affect children only)
  el.style.setProperty("--bs-primary", P);
  el.style.setProperty("--bs-primary-rgb", prgb);
  el.style.setProperty("--bs-link-color", P);
  el.style.setProperty("--bs-link-hover-color", shade(P, -8));
  el.style.setProperty("--bs-focus-ring-color", `rgba(${prgb}, .25)`);
  el.style.setProperty("--bs-btn-hover-bg", shade(P, -7));
  el.style.setProperty("--bs-btn-hover-border-color", shade(P, -9));
  el.style.setProperty("--bs-btn-active-bg", shade(P, -12));
  el.style.setProperty("--bs-btn-active-border-color", shade(P, -14));

  // 🔁 Mirror essentials to :root for legacy components & global CSS that read there
  if (el !== document.documentElement) {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", P);
    root.style.setProperty("--brand-accent", A);
    root.style.setProperty("--on-primary", onColorFor(P));
    root.style.setProperty("--on-accent", onColorFor(A));
    root.style.setProperty("--brand-accent-soft", hexToRgba(A, 0.08));
    root.style.setProperty("--accent-10", hexToRgba(A, 0.10));
    root.style.setProperty("--accent-18", hexToRgba(A, 0.18));
    root.style.setProperty("--accent-30", hexToRgba(A, 0.30));
    root.style.setProperty("--accent-35", hexToRgba(A, 0.35));
  }
};

const onColorFor = (hex) => {
  const h = normalizeHexAny(hex);
  if (!h) return "#fff";
  const n = parseInt(h.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#111" : "#fff";
};

/* ---------------- context + provider ---------------- */
const BrandCtx = createContext({
  primary: "#0d6efd",
  accent: "#6610f2",
  setPrimary: () => {},
  setAccent: () => {},
  setPalette: () => {},
});

/**
 * BrandProvider
 * Props:
 *  - ns?: string      -> localStorage namespace (e.g., "student:UID", "school:SCHOOLID")
 *  - scope?: string | HTMLElement | React.RefObject -> where to apply CSS variables
 */
export const BrandProvider = ({ children, ns = "", scope = undefined }) => {
  const prefix = ns ? `brand:${ns}` : "brand";

  const readLS = (key, fallback) => {
    try { return localStorage.getItem(`${prefix}.${key}`) || fallback; } catch { return fallback; }
  };
  const writeLS = (key, value) => {
    try {
      localStorage.setItem(`${prefix}.${key}`, value);
      // 💾 legacy/global compatibility
      if (key === "primary") localStorage.setItem("brand.primary", value);
      if (key === "accent")  { localStorage.setItem("brand.accent", value); localStorage.setItem("accentColor", value); }
    } catch {}
  };

  // Initial: prefer namespaced LS → CSS vars on scope → CSS vars on :root → defaults
  const scopeEl = resolveRootEl(scope);
  const initialPrimary =
    normalizeHexAny(readLS("primary")) ||
    readCssHex(scopeEl, "--brand-primary") ||
    readCssHex(document.documentElement, "--brand-primary") ||
    "#0d6efd";
  const initialAccent =
    normalizeHexAny(readLS("accent")) ||
    readCssHex(scopeEl, "--brand-accent") ||
    readCssHex(document.documentElement, "--brand-accent") ||
    shade(initialPrimary, -10);

  const [primary, setPrimary] = useState(normalizeHex(initialPrimary));
  const [accent, setAccent]   = useState(normalizeHex(initialAccent));

  // Rehydrate if namespace changes (e.g., switch user/role/school)
  useEffect(() => {
    const p = normalizeHexAny(readLS("primary")) || readCssHex(scopeEl, "--brand-primary") || "#0d6efd";
    const a = normalizeHexAny(readLS("accent"))  || readCssHex(scopeEl, "--brand-accent")  || shade(p, -10);
    setPrimary(normalizeHex(p));
    setAccent(normalizeHex(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ns]);

  // Apply + persist whenever either changes
  useEffect(() => {
    applyBrand(primary, accent, scope);
    writeLS("primary", normalizeHex(primary));
    writeLS("accent", normalizeHex(accent));
    // Dispatch a friendly event so any non-context UI can react
    try { window.dispatchEvent(new CustomEvent("brand:applied", { detail: { primary, accent, ns } })); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, accent, ns, scope]);

  // Convenience: set both to the same color
  const setPalette = (hex) => {
    const h = normalizeHex(hex);
    setPrimary(h);
    setAccent(h);
  };

  /* ------------------------- SUBSCRIPTIONS / BRIDGES ------------------------- */

  // 1) Custom event bridge from legacy pickers
  useEffect(() => {
    const onChanged = (e) => {
      const p = normalizeHexAny(e?.detail?.primary);
      const a = normalizeHexAny(e?.detail?.accent);
      if (p && p !== primary) setPrimary(p);
      if (a && a !== accent)  setAccent(a);
    };
    window.addEventListener("brand:changed", onChanged);
    return () => window.removeEventListener("brand:changed", onChanged);
  }, [primary, accent]);

  // 2) Cross-tab localStorage sync (also catches other namespaces via legacy keys)
  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key) return;
      if (e.key === `${prefix}.primary` || e.key === "brand.primary") {
        const p = normalizeHexAny(e.newValue);
        if (p && p !== primary) setPrimary(p);
      }
      if (e.key === `${prefix}.accent` || e.key === "brand.accent" || e.key === "accentColor") {
        const a = normalizeHexAny(e.newValue);
        if (a && a !== accent) setAccent(a);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [primary, accent, prefix]);

  // 3) Detect direct CSS var edits to the scoped element or :root
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const targetEls = Array.from(new Set([resolveRootEl(scope), document.documentElement].filter(Boolean)));
    const read = (el) => ({
      p: readCssHex(el, "--brand-primary"),
      a: readCssHex(el, "--brand-accent"),
    });

    const observers = targetEls.map((el) => {
      let prev = read(el);
      const ob = new MutationObserver(() => {
        const { p, a } = read(el);
        if (p && p !== primary) setPrimary(p);
        if (a && a !== accent)  setAccent(a);
        prev = { p, a };
      });
      ob.observe(el, { attributes: true, attributeFilter: ["style"] });
      return ob;
    });
    return () => observers.forEach((o) => o.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, accent, scope]);

  const value = useMemo(
    () => ({ primary, accent, setPrimary, setAccent, setPalette }),
    [primary, accent]
  );

  return <BrandCtx.Provider value={value}>{children}</BrandCtx.Provider>;
};

export const useBrand = () => useContext(BrandCtx);
